import { Component } from 'react';
import {
    Badge,
    Button,
    Dialog,
    IconButton,
    Pane,
    SearchInput,
    Spinner,
    Table,
    Text,
    EyeOpenIcon
} from 'evergreen-ui';
import Request from '../../../../util/Api';
import Config from '../../../../util/Configuration';
import Cookie from 'react-cookies';
import { toaster } from 'evergreen-ui';
import CatalogDetailModal from '../../../catalog/components/CatalogDetailModal';

const CATALOG_IMAGE_BUCKET = process.env.REACT_APP_CATALOG_IMAGE_BUCKET || 'https://krit-content.ams3.cdn.digitaloceanspaces.com/catalog-images';

let searchTimeout = null;

/**
 * Catalog Import Modal for Product Form
 * Allows searching and selecting a catalog item to import data from
 * 
 * Props:
 * - open: boolean - Whether the modal is visible
 * - onClose: function - Callback when modal is closed
 * - onImport: function(catalogItem) - Callback when a catalog item is selected for import
 */
export default class CatalogImport extends Component {
    state = {
        search: '',
        loading: false,
        results: [],
        totalCount: 0,
        selectedItem: null,
        detailModalOpen: false,
        refreshingItem: false,
        syncingItem: false
    };

    componentDidUpdate(prevProps) {
        // Reset state when modal opens
        if (this.props.open && !prevProps.open) {
            this.setState({
                search: '',
                results: [],
                totalCount: 0,
                selectedItem: null,
                detailModalOpen: false,
                refreshingItem: false,
                syncingItem: false
            });
        }
    }

    handleSearchChange = (e) => {
        const value = e.target.value;
        this.setState({ search: value });

        // Debounce search
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        if (value.length >= 2) {
            searchTimeout = setTimeout(() => {
                this.performSearch(value);
            }, 300);
        } else {
            this.setState({ results: [], totalCount: 0 });
        }
    };

    performSearch = async (query) => {
        this.setState({ loading: true });

        try {
            const result = await Request.get('/v2/catalog', {}, {
                q: query,
                _start: 0,
                _end: 20
            });

            this.setState({
                results: result.data || [],
                totalCount: result.count || 0,
                loading: false
            });
        } catch (error) {
            console.error('Error searching catalog:', error);
            this.setState({ loading: false, results: [] });
        }
    };

    getTypeBadgeColor = (type) => {
        switch (type) {
            case 'product': return 'blue';
            case 'tcg_single': return 'purple';
            case 'service': return 'green';
            default: return 'neutral';
        }
    };

    hasThumbnail = (item) => {
        return item.thumbnail && item.thumbnail !== '/images/placeholder-product.png';
    };

    getThumbnailUrl = (item) => {
        if (!this.hasThumbnail(item)) {
            return null;
        }
        return `${CATALOG_IMAGE_BUCKET}/thumb_${item.thumbnail}.jpg`;
    };

    renderThumbnail = (item, size = 40) => {
        if (this.hasThumbnail(item)) {
            return (
                <img
                    src={this.getThumbnailUrl(item)}
                    alt={item.name}
                    style={{
                        width: size,
                        height: size,
                        objectFit: 'cover',
                        borderRadius: 4
                    }}
                />
            );
        }
        return (
            <Pane
                width={size}
                height={size}
                backgroundColor="#f5f5f5"
                borderRadius={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <Text color="muted" size={300}>N/A</Text>
            </Pane>
        );
    };

    getSyncedImagesCount = (item) => {
        if (!item.images || item.images.length === 0) return 0;
        return item.images.filter(img => img.synced === 1).length;
    };

    handleSelectItem = (item) => {
        this.setState({ selectedItem: item });
    };

    handleViewDetails = (item, e) => {
        e.stopPropagation();
        this.setState({ selectedItem: item, detailModalOpen: true });
    };

    closeDetailModal = () => {
        this.setState({ detailModalOpen: false });
    };

    refreshSelectedItem = async () => {
        if (!this.state.selectedItem?.id) return;
        
        this.setState({ refreshingItem: true });
        
        try {
            const result = await Request.get(`/v2/catalog/${this.state.selectedItem.id}`, {}, {});
            
            if (result && result.id) {
                // Update both the selected item and in results list
                const updatedResults = this.state.results.map(item => 
                    item.id === result.id ? result : item
                );
                this.setState({ 
                    selectedItem: result,
                    results: updatedResults,
                    refreshingItem: false 
                });
            } else {
                this.setState({ refreshingItem: false });
            }
        } catch (error) {
            console.error('Error refreshing catalog item:', error);
            this.setState({ refreshingItem: false });
        }
    };

    syncSelectedItemImages = async () => {
        if (!this.state.selectedItem?.id) return;
        
        this.setState({ syncingItem: true });
        
        try {
            const response = await fetch(`${Config.host}/v2/catalog/${this.state.selectedItem.id}/sync-images`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'session': Cookie.load('nax-session')
                }
            });
            
            const result = await response.json();
            
            if (response.ok) {
                toaster.success(result.message || 'Imagini sincronizate cu succes');
                // Refresh the selected item to show newly synced images
                await this.refreshSelectedItem();
            } else {
                toaster.danger(result.error || 'Eroare la sincronizare');
            }
        } catch (error) {
            toaster.danger('Eroare la sincronizare: ' + error.message);
        }
        
        this.setState({ syncingItem: false });
    };

    handleImport = () => {
        if (this.state.selectedItem && this.props.onImport) {
            this.props.onImport(this.state.selectedItem);
            this.props.onClose();
        }
    };

    render() {
        const { open, onClose } = this.props;
        const { search, loading, results, totalCount, selectedItem, detailModalOpen } = this.state;

        return (
            <>
                <Dialog
                    isShown={open}
                    title="Import din Catalog"
                    onCloseComplete={onClose}
                    hasFooter={true}
                    width={900}
                    footer={
                        <Pane display="flex" justifyContent="space-between" width="100%">
                            <Button onClick={onClose}>
                                Anulează
                            </Button>
                            <Button 
                                appearance="primary"
                                disabled={!selectedItem}
                                onClick={this.handleImport}
                            >
                                Importă datele
                            </Button>
                        </Pane>
                    }
                >
                    <Pane>
                        <SearchInput
                            placeholder="Caută în catalog după nume, cod sau descriere..."
                            value={search}
                            onChange={this.handleSearchChange}
                            width="100%"
                            marginBottom={16}
                        />

                        {loading && (
                            <Pane display="flex" justifyContent="center" padding={20}>
                                <Spinner />
                            </Pane>
                        )}

                        {!loading && search.length < 2 && (
                            <Pane 
                                display="flex" 
                                alignItems="center" 
                                justifyContent="center" 
                                height={200}
                                background="tint1"
                                borderRadius={4}
                            >
                                <Text color="muted">Introduceți cel puțin 2 caractere pentru a căuta</Text>
                            </Pane>
                        )}

                        {!loading && search.length >= 2 && results.length === 0 && (
                            <Pane 
                                display="flex" 
                                alignItems="center" 
                                justifyContent="center" 
                                height={200}
                                background="tint1"
                                borderRadius={4}
                            >
                                <Text color="muted">Nu s-au găsit rezultate pentru "{search}"</Text>
                            </Pane>
                        )}

                        {!loading && results.length > 0 && (
                            <Pane>
                                <Text color="muted" marginBottom={10} display="block">
                                    {totalCount} rezultate găsite {totalCount > 20 && '(afișate primele 20)'}
                                </Text>
                                
                                <Table>
                                    <Table.Head>
                                        <Table.TextHeaderCell flexBasis={60} flexShrink={0} flexGrow={0}>
                                            Img
                                        </Table.TextHeaderCell>
                                        <Table.TextHeaderCell>
                                            Nume
                                        </Table.TextHeaderCell>
                                        <Table.TextHeaderCell flexBasis={100} flexShrink={0} flexGrow={0}>
                                            Tip
                                        </Table.TextHeaderCell>
                                        <Table.TextHeaderCell flexBasis={80} flexShrink={0} flexGrow={0}>
                                            Imagini
                                        </Table.TextHeaderCell>
                                        <Table.TextHeaderCell flexBasis={60} flexShrink={0} flexGrow={0}>
                                            
                                        </Table.TextHeaderCell>
                                    </Table.Head>
                                    <Table.Body maxHeight={400}>
                                        {results.map(item => (
                                            <Table.Row 
                                                key={item.id} 
                                                isSelectable 
                                                isSelected={selectedItem?.id === item.id}
                                                onSelect={() => this.handleSelectItem(item)}
                                            >
                                                <Table.Cell flexBasis={60} flexShrink={0} flexGrow={0}>
                                                    {this.renderThumbnail(item, 40)}
                                                </Table.Cell>
                                                <Table.TextCell>
                                                    <Text fontWeight={500}>{item.name}</Text>
                                                    {item.itemId && (
                                                        <Text display="block" color="muted" size={300}>
                                                            ID: {item.itemId}
                                                        </Text>
                                                    )}
                                                </Table.TextCell>
                                                <Table.Cell flexBasis={100} flexShrink={0} flexGrow={0}>
                                                    <Badge color={this.getTypeBadgeColor(item.type)}>
                                                        {item.type}
                                                    </Badge>
                                                </Table.Cell>
                                                <Table.TextCell flexBasis={80} flexShrink={0} flexGrow={0}>
                                                    {this.getSyncedImagesCount(item)} sync
                                                </Table.TextCell>
                                                <Table.Cell flexBasis={60} flexShrink={0} flexGrow={0}>
                                                    <IconButton
                                                        icon={EyeOpenIcon}
                                                        appearance="minimal"
                                                        onClick={(e) => this.handleViewDetails(item, e)}
                                                    />
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                            </Pane>
                        )}

                        {selectedItem && (
                            <Pane 
                                marginTop={16} 
                                padding={12} 
                                background="blueTint" 
                                borderRadius={4}
                                display="flex"
                                alignItems="center"
                                gap={12}
                            >
                                {this.renderThumbnail(selectedItem, 50)}
                                <Pane>
                                    <Text fontWeight={600}>Selectat: {selectedItem.name}</Text>
                                    <Text display="block" color="muted" size={300}>
                                        {this.getSyncedImagesCount(selectedItem)} imagini sincronizate
                                    </Text>
                                </Pane>
                            </Pane>
                        )}
                    </Pane>
                </Dialog>

                {/* Detail Modal for previewing catalog item */}
                <CatalogDetailModal
                    isShown={detailModalOpen}
                    item={selectedItem}
                    onClose={this.closeDetailModal}
                    showCreateButton={false}
                    onRefresh={this.refreshSelectedItem}
                    refreshing={this.state.refreshingItem}
                    onSyncImages={this.syncSelectedItemImages}
                    syncing={this.state.syncingItem}
                />
            </>
        );
    }
}
